import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

export default function ServiceEngagements() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [form, setForm] = useState({ client_name: '', service_id: '', engagement_type: '', source: 'manual', start_date: '', expected_end_date: '', expected_revenue: '', estimated_cost: '', notes: '' });
  const [services, setServices] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchEngagements = () => {
    if (!companyId) return;
    setLoading(true);
    let url = `${API}/companies/${companyId}/services/engagements?page=${page}&per_page=25`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    if (sourceFilter) url += `&source=${sourceFilter}`;

    fetch(url, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => {
        setEngagements(Array.isArray(d.data) ? d.data : []);
        setTotalPages(d.last_page || 1);
      })
      .catch(() => setEngagements([]))
      .finally(() => setLoading(false));
  };

  const fetchServices = () => {
    if (!companyId) return;
    fetch(`${API}/companies/${companyId}/services/catalog?per_page=100&status=active`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => setServices(Array.isArray(d.data) ? d.data : []))
      .catch(() => {});
  };

  useEffect(() => { fetchEngagements(); fetchServices(); }, [companyId, page]);

  const handleSubmit = () => {
    const method = editing ? 'PUT' : 'POST';
    const url = editing
      ? `${API}/companies/${companyId}/services/engagements/${editing.id}`
      : `${API}/companies/${companyId}/services/engagements`;
    fetch(url, { method, headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setShowForm(false); setEditing(null); resetForm(); fetchEngagements(); } })
      .catch(() => {});
  };

  const resetForm = () => setForm({ client_name: '', service_id: '', engagement_type: '', source: 'manual', start_date: '', expected_end_date: '', expected_revenue: '', estimated_cost: '', notes: '' });

  const openEdit = (e) => {
    setForm({ client_name: e.client_name || '', service_id: e.service_id || '', engagement_type: e.engagement_type || '', source: e.source || 'manual', start_date: e.start_date || '', expected_end_date: e.expected_end_date || '', expected_revenue: e.expected_revenue || '', estimated_cost: e.estimated_cost || '', notes: e.notes || '' });
    setEditing(e);
    setShowForm(true);
  };

  const viewDetail = (id) => {
    fetch(`${API}/companies/${companyId}/services/engagements/${id}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setDetail(d))
      .catch(() => {});
  };

  const doAction = (id, action, body = null) => {
    fetch(`${API}/companies/${companyId}/services/engagements/${id}/${action}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : null,
    })
      .then(r => r.ok ? r.json() : null)
      .then(() => { fetchEngagements(); if (detail && detail.id === id) viewDetail(id); })
      .catch(() => {});
  };

  const addNote = (id) => {
    if (!noteText.trim()) return;
    fetch(`${API}/companies/${companyId}/services/engagements/${id}/notes`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: noteText }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(() => { setNoteText(''); viewDetail(id); })
      .catch(() => {});
  };

  const fmt = (v) => '$' + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  const cardStyle = { background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', marginBottom: 20 };
  const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e2e8f0', fontSize: '0.8rem', color: '#64748b', fontWeight: 600 };
  const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '0.85rem' };
  const btnStyle = (bg) => ({ padding: '4px 12px', background: bg, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', marginRight: 4 });
  const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.85rem', width: '100%', boxSizing: 'border-box' };
  const badgeStyle = (color) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600, background: color + '20', color });
  const statusColor = { planned: '#94a3b8', active: '#22c55e', on_hold: '#f59e0b', completed: '#3b82f6', cancelled: '#ef4444', archived: '#6b7280' };

  if (detail) {
    return (
      <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <button onClick={() => setDetail(null)} style={{ ...btnStyle('#6b7280'), marginBottom: 16, padding: '6px 16px' }}>← Back</button>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700 }}>{detail.engagement_code}</h2>
              <div style={{ fontSize: '0.9rem', color: '#475569', marginTop: 4 }}>{detail.client_name}</div>
              <span style={{ ...badgeStyle(statusColor[detail.status] || '#94a3b8'), marginTop: 8, display: 'inline-block' }}>{(detail.status || '').replace('_', ' ')}</span>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {detail.status === 'planned' && <button onClick={() => doAction(detail.id, 'activate')} style={btnStyle('#22c55e')}>Activate</button>}
              {detail.status === 'active' && <button onClick={() => doAction(detail.id, 'hold', { reason: window.prompt('Hold reason:') })} style={btnStyle('#f59e0b')}>Hold</button>}
              {detail.status === 'on_hold' && <button onClick={() => doAction(detail.id, 'resume')} style={btnStyle('#22c55e')}>Resume</button>}
              {detail.status === 'active' && <button onClick={() => doAction(detail.id, 'complete')} style={btnStyle('#3b82f6')}>Complete</button>}
              {['planned', 'active', 'on_hold'].includes(detail.status) && <button onClick={() => doAction(detail.id, 'cancel', { reason: window.prompt('Cancel reason:') })} style={btnStyle('#ef4444')}>Cancel</button>}
              {detail.status === 'completed' && <button onClick={() => doAction(detail.id, 'archive')} style={btnStyle('#6b7280')}>Archive</button>}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Service</span><div style={{ fontWeight: 600 }}>{detail.service?.name || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Category</span><div style={{ fontWeight: 600 }}>{detail.category?.name || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Source</span><div style={{ fontWeight: 600 }}>{detail.source || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Type</span><div style={{ fontWeight: 600 }}>{(detail.engagement_type || '').replace('_', ' ') || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Service Owner</span><div style={{ fontWeight: 600 }}>{detail.service_owner?.name || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Account Owner</span><div style={{ fontWeight: 600 }}>{detail.account_owner?.name || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Start Date</span><div style={{ fontWeight: 600 }}>{detail.start_date || '—'}</div></div>
            <div><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>End Date</span><div style={{ fontWeight: 600 }}>{detail.expected_end_date || '—'}</div></div>
          </div>
        </div>

        {/* Financial Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div style={{ ...cardStyle, borderLeft: '4px solid #22c55e' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Expected Revenue</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#22c55e' }}>{fmt(detail.expected_revenue)}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #3b82f6' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Collected</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(detail.collected_revenue)}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #ef4444' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Actual Cost</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#ef4444' }}>{fmt(detail.actual_cost)}</div></div>
          <div style={{ ...cardStyle, borderLeft: '4px solid #8b5cf6' }}><div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Margin ({detail.margin_pct || 0}%)</div><div style={{ fontSize: '1.2rem', fontWeight: 700, color: detail.operational_margin >= 0 ? '#22c55e' : '#ef4444' }}>{fmt(detail.operational_margin)}</div></div>
        </div>

        {/* Notes */}
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>📝 Notes</h3>
          {Array.isArray(detail.notes) && detail.notes.map(n => (
            <div key={n.id} style={{ padding: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 8 }}>
              <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: 4 }}>{n.user?.name || 'System'} — {new Date(n.created_at).toLocaleDateString()}</div>
              <div style={{ fontSize: '0.9rem' }}>{n.note}</div>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Add a note..." style={{ ...inputStyle, flex: 1 }} onKeyDown={e => e.key === 'Enter' && addNote(detail.id)} />
            <button onClick={() => addNote(detail.id)} style={{ ...btnStyle('#3b82f6'), padding: '8px 16px' }}>Add</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>🤝 Service Engagements</h2>
        <button onClick={() => { resetForm(); setEditing(null); setShowForm(true); }} style={{ padding: '8px 20px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>+ New Engagement</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <input placeholder="Search by client or code..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchEngagements()} style={{ ...inputStyle, maxWidth: 260 }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }}>
          <option value="">All Statuses</option>
          <option value="planned">Planned</option>
          <option value="active">Active</option>
          <option value="on_hold">On Hold</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
          <option value="archived">Archived</option>
        </select>
        <select value={sourceFilter} onChange={e => { setSourceFilter(e.target.value); setPage(1); }} style={{ ...inputStyle, maxWidth: 160 }}>
          <option value="">All Sources</option>
          <option value="opportunity">Opportunity</option>
          <option value="direct_request">Direct Request</option>
          <option value="manual">Manual</option>
          <option value="contract">Contract</option>
          <option value="renewal">Renewal</option>
        </select>
        <button onClick={() => { setPage(1); fetchEngagements(); }} style={{ ...btnStyle('#3b82f6'), padding: '8px 16px' }}>Search</button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 32, width: 560, maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '1.2rem', fontWeight: 700 }}>{editing ? 'Edit Engagement' : 'New Engagement'}</h3>
            <div style={{ display: 'grid', gap: 14 }}>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Client Name *</label><input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} style={inputStyle} /></div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Service *</label>
                <select value={form.service_id} onChange={e => setForm({ ...form, service_id: e.target.value })} style={inputStyle}>
                  <option value="">— Select —</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.service_code})</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Source</label>
                  <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} style={inputStyle}>
                    <option value="manual">Manual</option><option value="opportunity">Opportunity</option><option value="direct_request">Direct Request</option><option value="contract">Contract</option><option value="renewal">Renewal</option>
                  </select>
                </div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Engagement Type</label>
                  <select value={form.engagement_type} onChange={e => setForm({ ...form, engagement_type: e.target.value })} style={inputStyle}>
                    <option value="">— Auto from service —</option><option value="one_time">One-Time</option><option value="fixed_term">Fixed Term</option><option value="recurring">Recurring</option><option value="ongoing">Ongoing</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Start Date</label><input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Expected End Date</label><input type="date" value={form.expected_end_date} onChange={e => setForm({ ...form, expected_end_date: e.target.value })} style={inputStyle} /></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Expected Revenue</label><input type="number" step="0.01" value={form.expected_revenue} onChange={e => setForm({ ...form, expected_revenue: e.target.value })} style={inputStyle} /></div>
                <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Estimated Cost</label><input type="number" step="0.01" value={form.estimated_cost} onChange={e => setForm({ ...form, estimated_cost: e.target.value })} style={inputStyle} /></div>
              </div>
              <div><label style={{ fontSize: '0.8rem', color: '#64748b', display: 'block', marginBottom: 4 }}>Notes</label><textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} style={{ ...inputStyle, minHeight: 50 }} /></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ ...btnStyle('#6b7280'), padding: '8px 20px' }}>Cancel</button>
              <button onClick={handleSubmit} style={{ ...btnStyle('#3b82f6'), padding: '8px 20px' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Engagements Table */}
      {initialLoading ? <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div> : (
        <div style={cardStyle}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Code</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Source</th>
                <th style={thStyle}>Start</th>
                <th style={thStyle}>Revenue</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {engagements.map(e => (
                <tr key={e.id}>
                  <td style={tdStyle}><span style={{ fontWeight: 600, cursor: 'pointer', color: '#3b82f6' }} onClick={() => viewDetail(e.id)}>{e.engagement_code}</span></td>
                  <td style={tdStyle}>{e.client_name}</td>
                  <td style={tdStyle}>{e.service?.name || '—'}</td>
                  <td style={tdStyle}><span style={badgeStyle(statusColor[e.status] || '#94a3b8')}>{(e.status || '').replace('_', ' ')}</span></td>
                  <td style={tdStyle}>{(e.source || '').replace('_', ' ')}</td>
                  <td style={tdStyle}>{e.start_date || '—'}</td>
                  <td style={tdStyle}>{fmt(e.expected_revenue)}</td>
                  <td style={tdStyle}>
                    <button onClick={() => openEdit(e)} style={btnStyle('#f59e0b')}>Edit</button>
                    <button onClick={() => viewDetail(e.id)} style={btnStyle('#3b82f6')}>View</button>
                  </td>
                </tr>
              ))}
              {engagements.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#94a3b8' }}>No engagements found</td></tr>}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ ...btnStyle('#6b7280'), opacity: page <= 1 ? 0.5 : 1 }}>Prev</button>
              <span style={{ padding: '4px 12px', fontSize: '0.85rem' }}>Page {page} of {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ ...btnStyle('#6b7280'), opacity: page >= totalPages ? 0.5 : 1 }}>Next</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
